import type { ReactNode } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CalendarDays, Clock, Phone, Quote as QuoteIcon } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { company } from "@/data/site";
import { AUTHOR, blogPosts, getPost, type Block } from "@/data/blogPosts";
import { seoHead, absoluteUrl, SITE_URL, BRAND } from "@/components/SEOHead";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return { slug: post.slug };
  },
  head: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) return {};
    const url = `${SITE_URL}/blog/${post.slug}`;
    const image = absoluteUrl(post.hero.src);
    return seoHead({
      title: post.title,
      description: post.metaDescription,
      path: `/blog/${post.slug}`,
      image: post.hero.src,
      keywords: post.keywords,
      type: "article",
      author: AUTHOR.name,
      publishedTime: post.datePublished,
      modifiedTime: post.dateModified,
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.h1,
          description: post.metaDescription,
          image: [image],
          datePublished: post.datePublished,
          dateModified: post.dateModified,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          url,
          author: {
            "@type": "Organization",
            name: AUTHOR.name,
            url: `${SITE_URL}${AUTHOR.bioPath}`,
          },
          publisher: {
            "@type": "Organization",
            name: BRAND,
            url: SITE_URL,
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
  },
  component: BlogPostPage,
});

/** Render inline **bold** and [text](href) markdown. */
function inline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] && m[2]) {
      const href = m[2];
      const external = /^https?:\/\//.test(href);
      nodes.push(
        external ? (
          <a
            key={`a${key++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="font-semibold text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
          >
            {m[1]}
          </a>
        ) : (
          <Link
            key={`a${key++}`}
            to={href}
            className="font-semibold text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
          >
            {m[1]}
          </Link>
        ),
      );
    } else if (m[3]) {
      nodes.push(
        <strong key={`b${key++}`} className="font-bold text-primary">
          {m[3]}
        </strong>,
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function BlockView({ block }: { block: Block }) {
  switch (block.type) {
    case "p":
      return <p className="text-[17px] leading-relaxed text-foreground/85">{inline(block.text)}</p>;
    case "h3":
      return <h3 className="mt-8 font-display text-xl font-bold text-primary">{block.text}</h3>;
    case "ul":
      return (
        <ul className="space-y-2.5">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3 text-[17px] leading-relaxed text-foreground/85">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
              <span>{inline(it)}</span>
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="space-y-3">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3 text-[17px] leading-relaxed text-foreground/85">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                {i + 1}
              </span>
              <span>{inline(it)}</span>
            </li>
          ))}
        </ol>
      );
    case "callout":
      return (
        <aside className="rounded-2xl border border-border border-l-4 border-l-accent bg-muted p-5 sm:p-6">
          <p className="font-display text-base font-bold uppercase tracking-wide text-primary">{block.title}</p>
          <p className="mt-2 text-[16px] leading-relaxed text-foreground">{inline(block.text)}</p>
        </aside>
      );
    case "quote":
      return (
        <blockquote className="relative rounded-2xl bg-primary px-6 py-7 text-primary-foreground sm:px-8">
          <QuoteIcon className="absolute right-5 top-5 h-8 w-8 text-primary-foreground/20" aria-hidden="true" />
          <p className="font-display text-lg font-semibold leading-relaxed text-balance">{block.text}</p>
          {block.cite && <footer className="mt-3 text-sm text-primary-foreground/70">- {block.cite}</footer>}
        </blockquote>
      );
  }
}

function BlogPostPage() {
  const { slug } = Route.useParams();
  const post = getPost(slug);
  if (!post) return null;

  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);
  const published = new Date(`${post.datePublished}T12:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <header className="bg-gradient-hero text-primary-foreground">
          <div className="mx-auto max-w-3xl px-4 pb-10 pt-28 sm:px-6 sm:pt-32 lg:px-8">
            <nav aria-label="Breadcrumb" className="text-sm text-primary-foreground/75">
              <Link to="/blog" className="hover:underline">
                Blog
              </Link>{" "}
              <span aria-hidden="true">›</span> <span>{post.category}</span>
            </nav>
            <h1 className="mt-4 font-display text-3xl font-extrabold text-balance sm:text-4xl lg:text-5xl">{post.h1}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-primary-foreground/80">
              <span>
                By{" "}
                <Link to={AUTHOR.bioPath} className="font-semibold text-white underline underline-offset-2">
                  {AUTHOR.name}
                </Link>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4" aria-hidden="true" />
                {published}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4" aria-hidden="true" />
                {post.readMinutes} min read
              </span>
            </div>
          </div>
        </header>

        <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          {/* Hero image */}
          <figure className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
            <img
              src={post.hero.src}
              alt={post.hero.alt}
              width={1200}
              height={630}
              className="aspect-[1200/630] w-full object-cover"
            />
            <figcaption className="px-5 py-3 text-sm text-muted-foreground">{post.hero.caption}</figcaption>
          </figure>

          {/* Intro */}
          <div className="mt-10 space-y-5">
            {post.intro.map((p, i) => (
              <p key={i} className="text-lg leading-relaxed text-foreground/90">
                {inline(p)}
              </p>
            ))}
          </div>

          {/* TL;DR */}
          <section aria-label="Summary" className="mt-10 rounded-3xl border border-accent/30 bg-accent/10 p-6 sm:p-7">
            <p className="font-display text-sm font-extrabold uppercase tracking-[0.18em] text-accent">TL;DR</p>
            <ul className="mt-4 space-y-2.5">
              {post.tldr.map((t, i) => (
                <li key={i} className="flex gap-3 text-[16px] leading-relaxed text-foreground/85">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
                  <span>{inline(t)}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Table of contents */}
          <nav aria-label="Table of contents" className="mt-8 rounded-3xl border border-border bg-card p-6">
            <p className="font-display text-sm font-extrabold uppercase tracking-[0.18em] text-primary">
              What this post covers
            </p>
            <ol className="mt-4 space-y-2">
              {post.sections.map((s, i) => (
                <li key={s.id} className="flex gap-3 text-[16px]">
                  <span className="font-bold text-accent">{i + 1}.</span>
                  <a href={`#${s.id}`} className="text-foreground/85 underline-offset-2 hover:text-accent hover:underline">
                    {s.h2}
                  </a>
                </li>
              ))}
              <li className="flex gap-3 text-[16px]">
                <span className="font-bold text-accent">{post.sections.length + 1}.</span>
                <a href="#faq" className="text-foreground/85 underline-offset-2 hover:text-accent hover:underline">
                  Frequently asked questions
                </a>
              </li>
            </ol>
          </nav>

          {/* Body */}
          <div className="mt-14 space-y-14">
            {post.sections.map((s, idx) => (
              <section key={s.id} id={s.id} className="scroll-mt-28">
                <h2 className="font-display text-2xl font-extrabold text-primary text-balance sm:text-[28px]">{s.h2}</h2>
                <div className="mt-5 space-y-5">
                  {s.blocks.map((b, i) => (
                    <BlockView key={i} block={b} />
                  ))}
                </div>
                {s.image && (
                  <figure className="mt-8 overflow-hidden rounded-2xl border border-border bg-card">
                    <img
                      src={s.image.src}
                      alt={s.image.alt}
                      width={1200}
                      height={630}
                      className="aspect-[1200/630] w-full object-cover"
                    />
                    <figcaption className="px-5 py-3 text-sm text-muted-foreground">{s.image.caption}</figcaption>
                  </figure>
                )}

                {/* Mid-article CTA, roughly halfway through */}
                {idx === Math.floor(post.sections.length / 2) - 1 && (
                  <div className="mt-12 rounded-3xl bg-primary px-6 py-8 text-center text-primary-foreground sm:px-10">
                    <p className="font-display text-xl font-bold text-balance sm:text-2xl">
                      {post.ctaType === "solar" ? "Want your roof checked before panels go up?" : "Want a roofer to look at it first?"}
                    </p>
                    <p className="mx-auto mt-2 max-w-xl text-sm text-primary-foreground/80">
                      Free inspection across Southern California, with photos of exactly what we find.
                    </p>
                    <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                      <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                        <Link to="/contact">Get My Free Estimate</Link>
                      </Button>
                      <Button asChild size="lg" variant="outline" className="border-white/50 bg-white/10 text-white hover:bg-white hover:text-primary">
                        <a href={company.phoneHref}>
                          <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
                          {company.phone}
                        </a>
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>

          {/* FAQ */}
          <section id="faq" className="mt-16 scroll-mt-28">
            <h2 className="font-display text-2xl font-extrabold text-primary sm:text-[28px]">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-5">
              {post.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-semibold text-primary">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[16px] leading-relaxed text-foreground/85">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Author bio */}
          <section className="mt-14 rounded-2xl border border-border border-l-4 border-l-accent bg-card p-6 shadow-sm sm:p-7">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-accent">About the author</p>
            <h2 className="mt-2 font-display text-xl font-bold text-primary">{AUTHOR.name}</h2>
            <p className="mt-2 text-[16px] leading-relaxed text-foreground">{AUTHOR.role}</p>
            <Link
              to={AUTHOR.bioPath}
              className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-accent hover:underline"
            >
              Read more about McKay Roofing &amp; Solar <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </section>

          {/* End CTA card */}
          <section className="mt-12 overflow-hidden rounded-3xl bg-gradient-hero px-6 py-10 text-center text-primary-foreground sm:px-10">
            <h2 className="font-display text-2xl font-extrabold text-balance sm:text-3xl">{post.cta.title}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-primary-foreground/85">{post.cta.text}</p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact">Get My Free Estimate</Link>
              </Button>
              <Button asChild size="lg" className="btn-glow bg-white text-primary hover:bg-white/90">
                <a href={company.phoneHref}>
                  <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
                  {company.phone}
                </a>
              </Button>
            </div>
          </section>

          {/* Related */}
          {related.length > 0 && (
            <section className="mt-16">
              <h2 className="font-display text-2xl font-extrabold text-primary">Keep reading</h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to="/blog/$slug"
                    params={{ slug: r.slug }}
                    className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-1 hover:border-accent hover:shadow-lg"
                  >
                    <img
                      src={r.hero.src}
                      alt={r.hero.alt}
                      width={1200}
                      height={630}
                      className="aspect-[16/10] w-full object-cover"
                    />
                    <span className="block p-4 font-display text-sm font-bold text-primary group-hover:text-accent">
                      {r.h1}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}
